import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import NotificationContext from '../context/NotificationContext';
import { Box, CircularProgress, Container, Button, Typography, Chip, Switch, FormControlLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import TeachersComponent from '../components/TeachersComponent';
import { useTranslation } from 'react-i18next';

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';

const TeachersPage = () => {
    const { authTokens, logoutUser, backendUrl, pageTitle } = useContext(AuthContext);
    const { showToast } = useContext(NotificationContext);
    const [teachers, setTeachers] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [showInactive, setShowInactive] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    document.title = pageTitle + ' - ' + t('teachers.title');

    const getTeachers = useCallback(async () => {
        try {
            const response = await fetch(backendUrl + 'api/teachers/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + String(authTokens.access),
                },
            });
            if (response.status === 401) { logoutUser(); return; }
            const data = await response.json();
            setTeachers(data);
        } catch { showToast(t('common.network_error'), 'error'); }
        setLoading(false);
    }, []);

    useEffect(() => { getTeachers(); }, []);

    const onTeacherDelete = () => { getTeachers(); };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={44} thickness={3} sx={{ color: AMBER }} />
                    <Typography sx={{ mt: 2, fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#64748b', letterSpacing: '0.02em' }}>
                        {t('teachers.loading')}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: '#f1f5f9', pt: 4, pb: 6 }}>
            <Container maxWidth="xl">

                {/* Page Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: '12px', background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`, boxShadow: `0 4px 14px rgba(15,45,94,0.25)`, flexShrink: 0 }}>
                            <SchoolIcon sx={{ color: AMBER, fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.5rem', color: NAV_BG, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                                {t('teachers.title')}
                            </Typography>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', fontWeight: 400, mt: 0.1 }}>
                                {t('teachers.subtitle')}
                            </Typography>
                        </Box>
                        <Chip label={teachers.length} size="small" sx={{ ml: 0.5, fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.75rem', background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, boxShadow: `0 2px 8px ${AMBER_GLOW}`, border: 'none', height: 24, alignSelf: 'flex-start', mt: 0.25 }} />
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/add-teacher')}
                        sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', borderRadius: '10px', px: 2.5, py: 1, background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, boxShadow: `0 4px 14px ${AMBER_GLOW}`, border: 'none', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`, boxShadow: `0 6px 20px rgba(245,158,11,0.35)`, transform: 'translateY(-1px)' } }}
                    >
                        {t('teachers.add')}
                    </Button>
                </Box>

                {/* Filters */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <FormControlLabel
                        control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }} />}
                        label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#92400e' }}>{t('teachers.show_inactive')}</Typography>}
                    />
                </Box>

                {/* Teachers Table */}
                <TeachersComponent onTeacherDelete={onTeacherDelete} teachers={showInactive ? teachers : teachers.filter(t => t.is_active)} />

            </Container>
        </Box>
    );
};

export default TeachersPage;
